import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Facebook, Instagram, Youtube, LayoutDashboard, MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { selectIsAuthenticated } from '../store/slices/authSlice';

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6c0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64c0 3.33 2.76 5.7 5.69 5.7c3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z" />
  </svg>
);

const SOCIAL_LINKS = [
  { name: 'Facebook', href: 'https://www.facebook.com/zerinwithyou', icon: Facebook },
  { name: 'Instagram', href: 'https://www.instagram.com/zerinsfashion/', icon: Instagram },
  { name: 'YouTube', href: 'https://www.youtube.com/@Zerinwithyou', icon: Youtube },
  { name: 'TikTok', href: 'https://www.tiktok.com/@zerins78', icon: TikTokIcon },
];

const ADDRESS = "196/5, Shantibag, Malibagh, Dhaka.";
const ADDRESS_MAPS_HREF = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`;
const WHATSAPP_HREF = 'https://wa.me/8801840001034';
const WHATSAPP_DISPLAY = '+880 1840-001034';
const PHONE_HREF = 'tel:+8801618871511';
const PHONE_DISPLAY = '+880 1618-871511';
const EMAIL = 'zerinsofficial@gmail.com';

const Footer = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const goToAdmin = () => navigate(isAuthenticated ? '/admin' : '/admin/login');

  return (
    <footer className="bg-gray-900 text-gray-300 pb-16 md:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Brand & social */}
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Zerin's</h2>
            <p className="text-sm text-gray-400 mb-4 max-w-xs">
              Elegant dresses, thoughtfully delivered across Bangladesh.
            </p>
            <div className="flex items-center gap-2">
              {SOCIAL_LINKS.map(({ name, href, icon: Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="p-2 rounded-full text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wide mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" />
                <a
                  href={ADDRESS_MAPS_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {ADDRESS}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <MessageCircle className="w-4 h-4 flex-shrink-0 text-gray-500" />
                <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  WhatsApp: {WHATSAPP_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 flex-shrink-0 text-gray-500" />
                <a href={PHONE_HREF} className="hover:text-white transition-colors">
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 flex-shrink-0 text-gray-500" />
                <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors">
                  {EMAIL}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-500 text-center md:text-left">
            &copy; 2026 Zerin's. All rights reserved. ·{' '}
            <Link to="/terms-and-conditions" className="underline underline-offset-2 hover:text-gray-300 transition-colors">
              Terms &amp; Conditions
            </Link>
          </p>

          <button
            onClick={goToAdmin}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
          >
            <LayoutDashboard className="w-4 h-4" />
            Admin Panel
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
